import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ValidatorFn} from '@angular/forms';
import {Subject} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {NameValueDTO} from 'src/app/modules/common/models/NameValueDTO';
import {NameValueCodeDTO} from '../../../models/NameValueCodeDTO';
import {HelpDTO} from 'src/app/modules/other/models/HelpDTO';
import {HelpService} from 'src/app/modules/other/services/help.service';
import {ErrorDialogComponent} from 'src/app/modules/common/dialogs/error-dialog/error-dialog.component';
import {Common} from 'src/app/modules/common/util/Util';
import {LookupService} from 'src/app/modules/common/services/lookup.service';
import {KeycloakService} from 'keycloak-angular';
import {DisplayStrings} from 'src/app/modules/common/constants/DisplayStrings';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';
import {ContactArrayDTO} from '../../../../ent/models/ContactsArrayDTO';
import {ContactDTO} from '../../../../ent/models/ContactDTO';

// Handling Specific Controls from Shared Contact Component
export interface ContactFormInputs {
  title?: NameValueDTO | NameValueCodeDTO;
  firstName?: string;
  middleName?: string;
  lastName?: string;
}
export function minimumContactsValidator(contacts: any[]): ValidatorFn {
  return (): { [key: string]: any } | null => {
    return contacts && contacts.length > 0 ? null : {minimumContacts: true};
  };
}

@Component({
  selector: 'greydom-contact-form',
  templateUrl: './contact-form.component.html',
  styleUrls: [
    './contact-form.component.scss',
    '../../../../../../styles/global/_add-view-edit-styles.scss'
  ]
})
export class ContactFormComponent implements OnChanges, OnInit, AfterViewChecked, OnDestroy {
  readonly destroyed$ = new Subject();
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly lookupValue = LookupValue;
  readonly listFormRowHeight = '60px';

  @Input() data: { contacts: ContactArrayDTO[] };
  @Input() formGroup: FormGroup;
  @Input() formInputs?: ContactFormInputs;

  contactFormGroup: FormGroup;
  designationFormControl = new FormControl();
  titleFormControl = new FormControl();
  firstNameFormControl = new FormControl();
  middleNameFormControl = new FormControl();
  lastNameFormControl = new FormControl();
  emailFormControl = new FormControl();
  mobileFormControl = new FormControl();
  landlineFormControl = new FormControl();
  doNotOverwriteContact = new FormControl();

  contact: ContactDTO;
  contactMessage: string;
  selectedContact = null;
  selectedContacts: ContactArrayDTO[];
  contactErrorMessages = DisplayStrings.contactErrorMessages;
  contactsRequiredFieldsSelected = true;

  // Lookup values
  designations = [];
  titles = [];

  helpInfo: HelpDTO[];
  userFirstLastName = null;

  constructor(
    private changeDetect: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private helpService: HelpService,
    private lookupService: LookupService,
    private keycloakService: KeycloakService,
  ) {
    this.selectedContacts = [];
    this.contactMessage = this.contactErrorMessages.minContactError;
    this.contactErrorMessages = DisplayStrings.contactErrorMessages;

    // FormGroups:
    this.contactFormGroup = this.formBuilder.group({
      designation: this.designationFormControl,
      title: this.titleFormControl,
      firstName: this.firstNameFormControl,
      middleName: this.middleNameFormControl,
      lastName: this.lastNameFormControl,
      email: this.emailFormControl,
      mobile: this.mobileFormControl,
      landline: this.landlineFormControl,
      doNotOverwrite: this.doNotOverwriteContact
    }, {validator: [minimumContactsValidator(this.selectedContacts)]});
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && changes.data.currentValue) {
      this.data = changes.data.currentValue;
      this.setFormsForEdit(this.data);
    }

    if (changes.formInputs?.currentValue) {
      const inputs = changes.formInputs.currentValue;
      if (inputs.title) {
        this.setTitleIfAvailable(inputs.title);
      }
      if (inputs.firstName) {
        this.firstNameFormControl.setValue(inputs.firstName, {emitEvent: false});
      }
      if (inputs.middleName) {
        this.middleNameFormControl.setValue(inputs.middleName, {emitEvent: false});
      }
      if (inputs.lastName) {
        this.lastNameFormControl.setValue(inputs.lastName, {emitEvent: false});
      }
    }
  }

  ngOnInit(): void {
    this.keycloakService.loadUserProfile().then((profile) => {
      this.userFirstLastName = `${profile.firstName} ${profile.lastName}`;
    });

    this.getListOfLookupValues(this.lookupValue.DesignationProvider);
    this.lookupService.getListOfLookupValues(this.lookupValue.Title).subscribe(
      (response: NameValueDTO[]) => {
        this.titles = response;
        if (this.formInputs?.title) {
          this.setTitleIfAvailable(this.formInputs.title);
        }
      }
    );
  }

  ngAfterViewChecked(): void {
    this.changeDetect.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  setTitleIfAvailable(titleInput: NameValueDTO | NameValueCodeDTO) {
    if (titleInput && this.titles.length) {
      const matchingTitle = this.titles.find(t => t.name === titleInput?.name);
      if (matchingTitle) {
        this.titleFormControl.setValue(matchingTitle, { emitEvent: false });
      }
    }
  }

  getHelpInfo(componentName: string) {
    this.helpService.getListOfHelpInfo(componentName).subscribe(
      (response) => {
        this.helpInfo = response;
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.handleError(error.businessMessage);
      }
    );
  }

  setFormsForEdit(data) {
    if (data.contacts?.length) {
      this.selectedContacts = data.contacts.map(element => {
        const contactElement: ContactArrayDTO = {
          ...element,
          updated: new Date(),
          updatedBy: this.userFirstLastName,
          created: element.created || new Date(),
          createdBy: element.createdBy || this.userFirstLastName
        };

        // Handle both contact and contactDTO cases
        const contactData = element.contact || element.contactDTO;

        if (contactData) {
          contactElement.contact = {
            ...contactData,
            updated: new Date(),
            updatedBy: this.userFirstLastName,
            created: contactData.created || new Date(),
            createdBy: contactData.createdBy || this.userFirstLastName
          };
        }

        return contactElement;
      });
      this.clearContactsFields();
    }

    this.contactFormGroup = this.formBuilder.group({
      designation: this.designationFormControl,
      title: this.titleFormControl,
      firstName: this.firstNameFormControl,
      middleName: this.middleNameFormControl,
      lastName: this.lastNameFormControl,
      email: this.emailFormControl,
      mobile: this.mobileFormControl,
      landline: this.landlineFormControl,
      doNotOverwrite: this.doNotOverwriteContact
    }, {validator: [minimumContactsValidator(this.selectedContacts)]});
  }

  getListOfLookupValues(entity: string): void {
    this.lookupService.getListOfLookupValues(entity).subscribe(
      (response: NameValueDTO[]) => {
        if (entity === this.lookupValue.DesignationProvider) {
          this.designations = response;
        }
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.handleError(error.businessMessage);
      }
    );
  }

  getInfoTooltip(inputName: string): string {
    let message = '';
    if (this.helpInfo) {
      const helpInfo = this.helpInfo.find((help) => help.inputName === inputName);
      message = helpInfo ? helpInfo.inputInfo : '';
    }
    return message;
  }

  getContactDisplayString(contactElement: ContactArrayDTO): string {
    if (!contactElement) {
      return '';
    }
    const contact = contactElement.contact || contactElement.contactDTO;
    if (!contact) {
      return '';
    }

    const middleName = contact.middleName ? ` ${contact.middleName}` : '';
    const emailAddress = contact.emailAddress ? ` ${contact.emailAddress}` : contact.eMailAddress ?? '';
    return `${contact.designation?.name} - ${contact.title?.name} ${contact.firstName}${middleName} ${contact.lastName} -
    ${emailAddress} - Mobile: ${contact.mobileNumber} - Landline: ${contact.telephoneNumber}`;
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 10000
    });
  }

  handleError(errorMessage: string) {
    this.dialog.open(ErrorDialogComponent, {
      width: '40vw',
      data: errorMessage
    });
  }

  onInputChangedCheckLandlineNumber() {
    if (isNaN(Number(this.landlineFormControl.value))) {
      this.contactMessage = this.contactErrorMessages.landlinePatternError;
      this.contactsRequiredFieldsSelected = false;
      return;
    }
    if (this.landlineFormControl.value && this.landlineFormControl.value.length !== 10) {
      this.contactMessage = this.contactErrorMessages.landlineMinLengthError;
      this.contactsRequiredFieldsSelected = false;
      return;
    }

    this.contactsRequiredFieldsSelected = true;

  }

  onInputChangedCheckMobileNumber() {
    if (isNaN(Number(this.mobileFormControl.value))) {
      this.contactMessage = this.contactErrorMessages.mobilePatternError;
      this.contactsRequiredFieldsSelected = false;
      return;
    }
    if (this.mobileFormControl.value && this.mobileFormControl.value.length !== 10) {
      this.contactMessage = this.contactErrorMessages.mobileMinLengthError;
      this.contactsRequiredFieldsSelected = false;
      return;
    }

    this.contactsRequiredFieldsSelected = true;

  }

  addContact() {
    if (this.designationFormControl.value != null && this.titleFormControl.value != null
      && this.firstNameFormControl.value != null && this.lastNameFormControl.value != null
      && this.emailFormControl.value != null && this.mobileFormControl.value != null && this.landlineFormControl.value != null) {
      this.contactsRequiredFieldsSelected = true;
      this.contact = {
        designation: this.designationFormControl.value,
        title: this.titleFormControl.value,
        firstName: this.firstNameFormControl.value,
        middleName: this.middleNameFormControl.value,
        lastName: this.lastNameFormControl.value,
        emailAddress: this.emailFormControl.value,
        mobileNumber: this.mobileFormControl.value,
        telephoneNumber: this.landlineFormControl.value,
        doNotOverwrite: !!this.doNotOverwriteContact.value ? 1 : 0,
        areaCode: null,
        communicationPlan: null,
        contactId: null,
        countryDialingCode: null,
        created: new Date(),
        createdBy: this.userFirstLastName,
        tenantId: null,
        mobilePrefix: null,
        updated: null,
        updatedBy: null
      };

      const contactLink: ContactArrayDTO = {
        contact: this.contact,
        created: new Date(),
        createdBy: this.userFirstLastName,
        startDate: new Date('1970-01-01'),  // Set to null as per suggestion
        endDate: null,
        updated: null,
        updatedBy: null,
        tenantId: null
      };

      this.selectedContact = null;
      this.selectedContacts.push(contactLink);
      this.clearContactsFields();
    }
    else {
      this.contactMessage = this.contactErrorMessages.contactMandatoryError;
      this.contactsRequiredFieldsSelected = false;

    }
  }

  editContact(contactElement: ContactArrayDTO) {
    if (contactElement) {
      if (this.selectedContact) {
        this.selectedContacts.push(this.selectedContact);
        this.selectedContact = null;
      }
      this.selectedContact = contactElement;
      const contact = contactElement.contact || contactElement.contactDTO;
      if (contact) {
        const designation = this.designations.find(item => item.value === contact.designation.value);
        const title = this.titles.find(item => item.value === contact.title.value);
        this.designationFormControl.setValue(designation);
        this.titleFormControl.setValue(title);
        this.firstNameFormControl.setValue(contact.firstName);
        this.middleNameFormControl.setValue(contact.middleName);
        this.lastNameFormControl.setValue(contact.lastName);
        this.emailFormControl.setValue(contact.emailAddress || contact?.eMailAddress);
        this.mobileFormControl.setValue(contact.mobileNumber);
        this.landlineFormControl.setValue(contact.telephoneNumber);
        this.doNotOverwriteContact.setValue(!!contact.doNotOverwrite);
        this.selectedContacts = this.selectedContacts.filter(item => {
          const itemContact = item.contact || item.contactDTO;
          return JSON.stringify(itemContact) !== JSON.stringify(contact);
        });
      }
      if (this.selectedContacts.length === 0) {
        this.contactFormGroup = this.formBuilder.group({
          designation: this.designationFormControl,
          title: this.titleFormControl,
          firstName: this.firstNameFormControl,
          middleName: this.middleNameFormControl,
          lastName: this.lastNameFormControl,
          email: this.emailFormControl,
          mobile: this.mobileFormControl,
          landline: this.landlineFormControl,
          doNotOverwrite: this.doNotOverwriteContact
        }, {validator: [minimumContactsValidator(this.selectedContacts)]});
      }
    }
  }

  clearContactsFields() {
    this.contactFormGroup.reset();
  }

  resetContactFormGroup() {
    this.contactFormGroup.reset();
  }

  removeContact(contact) {
    const index = this.selectedContacts.indexOf(contact, 0);
    if (index > -1) {
      this.selectedContacts.splice(index, 1);
    }
    if (this.selectedContacts.length === 0) {
      this.contactFormGroup = this.formBuilder.group({
        designation: this.designationFormControl,
        title: this.titleFormControl,
        firstName: this.firstNameFormControl,
        middleName: this.middleNameFormControl,
        lastName: this.lastNameFormControl,
        email: this.emailFormControl,
        mobile: this.mobileFormControl,
        landline: this.landlineFormControl
      }, {validator: [minimumContactsValidator(this.selectedContacts)]});
    }
  }

  private getContactFromElement(contactElement: ContactArrayDTO): ContactDTO {
    return contactElement.contact || contactElement.contactDTO;
  }
  // Custom Methods to Access Via ViewChild:
  getPayload(): { contacts: ContactArrayDTO[] } {
    return {
      contacts: this.selectedContacts?.length ? this.selectedContacts.map(contactElement => {
        const { contactDTO, ...rest } = contactElement as ContactArrayDTO;
        return {
          ...rest,
          contact: this.getContactFromElement(contactElement)
        };
      }) : []
    };
  }

  getPayloadAsDTO(): { contacts: ContactArrayDTO[] } {
    // Backend accepts as addressDTO rather than address  see getPayload Method
    // Needs to fix BE: PHG and Broker both inorder to utilise getPayload.

    return {
      contacts: this.selectedContacts?.length ? this.selectedContacts.map(contactElement => {
        const { contact, ...rest } = contactElement as ContactArrayDTO;
        return {
          ...rest,
          contactDTO: this.getContactFromElement(contactElement)
        };
      }) : []
    };
  }
}
